// Music and ambience at the table (issue 1161), as the browser uses them. The table's sound arrives with the snapshot
// and every change to it (`foldAudio`); a track's bytes are fetched WITH the bearer (`authFetch`) and played from an
// object URL — a media element cannot carry a bearer, and no track is ever a public address. Each person's mixer (a
// volume a channel, and mute) lives in this browser alone: read and written in try/catch, never sent anywhere.
import { AUDIO_LIMITS, isAudioBody, normaliseAudio, reduceAudio, type AudioChannel, type AudioInfo, type AudioState, type ChannelPlay, type ProjectedEvent, normaliseCues, type Cue } from '@dragons-table/vtt-shared';
import { requestJson } from './api';
import { authFetch } from './session';

// ── The table's sound ──────────────────────────────────────────────────────

/** The sound moved by one event: the DM (and a co-DM with `notes`) reduces the body; anyone else takes the sound sent whole. */
export function foldAudio(cur: AudioState, ev: ProjectedEvent): AudioState {
  if (ev.kind === 'audio') return normaliseAudio(ev.audio);
  if (ev.kind === 'dm.event' && isAudioBody(ev.body)) return reduceAudio(cur, ev.body);
  return cur;
}

/**
 * Where a track should be now, in seconds: how long since it started (the server's `startAt`, on this screen's clock
 * corrected by `skewMs`), round the track when it loops, at most its end otherwise. Close to a few seconds; no more.
 */
export function playOffset(play: Pick<ChannelPlay, 'startAt' | 'loop' | 'tracks'>, durationSec: number, nowMs: number, skewMs = 0): number {
  const elapsed = Math.max(0, (nowMs - skewMs - Date.parse(play.startAt)) / 1000);
  if (!Number.isFinite(durationSec) || durationSec <= 0) return 0;
  if (play.loop && play.tracks.length === 1) return elapsed % durationSec;
  return Math.min(elapsed, durationSec);
}

/** Whether an effect fired at `startAt` is still worth playing on arrival (a late joiner hears no old bang). */
export const effectIsFresh = (startAt: string, nowMs: number, skewMs = 0): boolean => nowMs - skewMs - Date.parse(startAt) < 10_000;

// ── Each person's mixer ────────────────────────────────────────────────────

export interface Mixer { music: number; ambience: number; effect: number; muted: boolean }
export const MIXER_KEY = 'vtt.sound.mixer';
export const defaultMixer = (): Mixer => ({ music: 0.6, ambience: 0.6, effect: 0.8, muted: false });

const clamp01 = (v: unknown, fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback);

/** This browser's mixer; the defaults when nothing is stored, the storage is blocked, or what is stored is unreadable. */
export function loadMixer(): Mixer {
  const d = defaultMixer();
  try {
    const raw = window.localStorage.getItem(MIXER_KEY);
    if (!raw) return d;
    const v = JSON.parse(raw) as Partial<Mixer>;
    return { music: clamp01(v.music, d.music), ambience: clamp01(v.ambience, d.ambience), effect: clamp01(v.effect, d.effect), muted: v.muted === true };
  } catch {
    return d;
  }
}

const listeners = new Set<(m: Mixer) => void>();
/** Keep the mixer in this browser (if it lets us) and tell every surface showing it. Nothing is sent. */
export function saveMixer(m: Mixer): void {
  try { window.localStorage.setItem(MIXER_KEY, JSON.stringify(m)); } catch { /* blocked storage: this session only */ }
  for (const fn of listeners) fn(m);
}
export function onMixer(fn: (m: Mixer) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** The volume a channel plays at for this person: 0 when muted. */
export const volumeOf = (m: Mixer, channel: AudioChannel): number => (m.muted ? 0 : m[channel]);

// ── The DM's library ───────────────────────────────────────────────────────

export interface Room { used: number; cap: number }
export type SoundUpload = { kind: 'ok'; track: AudioInfo; room: Room } | { kind: 'refused'; reason: string } | { kind: 'error' };

export async function uploadSound(name: string, file: Blob, fetchImpl: typeof fetch = fetch): Promise<SoundUpload> {
  try {
    const res = await authFetch(`/api/audio?name=${encodeURIComponent(name)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/octet-stream', Accept: 'application/json' }, body: file,
    }, fetchImpl);
    const body = (await res.json().catch(() => null)) as { track?: AudioInfo; room?: Room; error?: string; reason?: string } | null;
    if (res.status === 201 && body?.track && body.room) return { kind: 'ok', track: body.track, room: body.room };
    if (body?.error === 'refused' && typeof body.reason === 'string') return { kind: 'refused', reason: body.reason };
    if (res.status === 413) return { kind: 'refused', reason: `This file is too big. The limit for one sound is ${AUDIO_LIMITS.bytes / (1024 * 1024)} MB.` };
    if (res.status === 403) return { kind: 'refused', reason: 'Only a DM uploads sounds.' };
    return { kind: 'error' };
  } catch {
    return { kind: 'error' };
  }
}

export async function listSounds(fetchImpl: typeof fetch = fetch): Promise<{ tracks: AudioInfo[]; room: Room } | null> {
  const { status, body } = await requestJson('/api/audio', { method: 'GET' }, fetchImpl);
  const b = body as { tracks?: unknown; room?: Room } | null;
  return status === 200 && Array.isArray(b?.tracks) && b?.room ? { tracks: b.tracks as AudioInfo[], room: b.room } : null;
}

export async function deleteSound(id: string, fetchImpl: typeof fetch = fetch): Promise<Room | null> {
  const { status, body } = await requestJson(`/api/audio/${id}`, { method: 'DELETE' }, fetchImpl);
  const room = (body as { room?: Room } | null)?.room;
  return status === 200 && room ? room : null;
}

/** The table's sound cues (issue 1162), the DM's alone: null when they cannot be read. */
export async function listCues(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<Cue[] | null> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/cues`, { method: 'GET' }, fetchImpl);
  const cues = (body as { cues?: unknown } | null)?.cues;
  return status === 200 && Array.isArray(cues) ? normaliseCues(cues) : null;
}

/** "12.3 of 100 MB used" — one decimal, as the server's refusal says it. */
export const roomWords = (room: Room): string => `${(room.used / (1024 * 1024)).toFixed(1)} of ${Math.round(room.cap / (1024 * 1024))} MB used`;

// A track's bytes, fetched once and kept as an object URL while the table wants it: `releaseSounds` revokes the rest.
const urls = new Map<string, Promise<string | null>>();

/**
 * Release every track's object URL but those in `keep` (the second 2026-10-10 review: none was ever revoked, so a long
 * session held every track it had played). The player keeps its playlists' tracks and the effects still sounding, and
 * releases every one when it leaves the table. A released track is fetched again if it is wanted again.
 */
export function releaseSounds(keep: ReadonlySet<string>): void {
  for (const [id, url] of [...urls]) {
    if (keep.has(id)) continue;
    urls.delete(id);
    void url.then((u) => { if (u) URL.revokeObjectURL(u); });
  }
}
export function soundUrl(id: string, fetchImpl: typeof fetch = fetch): Promise<string | null> {
  const had = urls.get(id);
  if (had) return had;
  const got = (async () => {
    try {
      const res = await authFetch(`/api/audio/${id}`, { method: 'GET' }, fetchImpl);
      if (!res.ok) return null;
      return URL.createObjectURL(await res.blob());
    } catch {
      return null;
    }
  })();
  urls.set(id, got);
  // A refusal is not kept: the track may be in play a moment later.
  void got.then((u) => { if (u === null && urls.get(id) === got) urls.delete(id); });
  return got;
}
